import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import './MyCoursesPage.css';
import { log, logError } from '../../utils/logger';

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  const clean = String(imagePath).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(clean)) return clean;
  const base = getApiBaseUrl();
  if (clean.startsWith('/')) return `${base}${clean}`;
  if (clean.startsWith('uploads/')) return `${base}/${clean}`;
  return `${base}/uploads/${clean}`;
};

const MyCoursesPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { userId } = useParams(); // Para detectar si intenta acceder a biblioteca de otro usuario
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Validación 1: Usuario no logueado
    if (!isAuthenticated || !user || !token) {
      log('⚠️ Usuario no autenticado, redirigiendo a login...');
      navigate('/login', { 
        state: { 
          from: '/my-courses',
          message: 'Debes iniciar sesión para ver tus cursos'
        } 
      });
      return;
    }

    // Validación 2: Intentar acceder a biblioteca de otro usuario
    if (userId && parseInt(userId) !== user.id) {
      log('⚠️ Intento de acceder a biblioteca de otro usuario, redirigiendo a login...');
      notify({
        message: 'No tienes permiso para acceder a la biblioteca de otro usuario',
        type: 'warning',
      });
      navigate('/login', { 
        state: { 
          from: '/my-courses',
          message: 'Debes iniciar sesión con tu cuenta para ver tus cursos'
        } 
      });
      return;
    }

    // Cargar cursos del usuario
    fetchUserCourses();
    
    // Recargar cuando la página vuelve a tener foco (para actualizar progreso)
    const handleFocus = () => {
      log('🔄 Página recuperó foco, recargando cursos para actualizar progreso...');
      fetchUserCourses();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, user, token, userId, navigate]);

  const fetchUserCourses = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('/api/user/courses/progress'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        // Token inválido o expirado
        navigate('/login', { 
          state: { 
            from: '/my-courses',
            message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente'
          } 
        });
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al cargar cursos (${response.status})`);
      }

      const data = await response.json();
      setCourses(data.courses || []);
    } catch (err) {
      logError('[MyCoursesPage] Error cargando cursos:', err);
      setError(err.message || 'Error al cargar tus cursos');
      notify({
        message: err.message || 'Error al cargar tus cursos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCourseClick = async (courseId) => {
    if (!token) {
      navigate('/login', { 
        state: { 
          from: `/courses/${courseId}`,
          message: 'Debes iniciar sesión para acceder al curso'
        } 
      });
      return;
    }

    // Validación 4: Verificar que el usuario tiene acceso al curso
    try {
      const response = await fetch(buildApiUrl(`/api/user/check-course/${courseId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.hasCourse) {
          // Usuario tiene acceso, navegar a la página de contenido del curso
          navigate(`/my-courses/${courseId}/content`);
        } else {
          // Usuario no tiene acceso, redirigir a tienda
          notify({
            message: 'No tienes acceso a este curso. Visita la tienda para adquirirlo.',
            type: 'warning',
          });
          navigate('/courses');
        }
      } else if (response.status === 404 || response.status === 401) {
        // Curso no encontrado o usuario no tiene acceso
        notify({
          message: 'No tienes acceso a este curso. Visita la tienda para adquirirlo.',
          type: 'warning',
        });
        navigate('/courses');
      } else {
        throw new Error('Error verificando acceso al curso');
      }
    } catch (err) {
      logError('[MyCoursesPage] Error verificando acceso:', err);
      // En caso de error, intentar navegar de todas formas (el backend validará)
      navigate(`/my-courses/${courseId}/content`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return '#28a745'; // Verde
    if (progress >= 50) return '#ffc107'; // Amarillo
    return '#17a2b8'; // Azul
  };

  // Validación 1: Usuario no logueado (ya manejado en useEffect, pero mostrar loading mientras redirige)
  if (!isAuthenticated || !user || !token) {
    return (
      <div className="my-courses-page">
        <div className="my-courses-container">
          <div className="my-courses-loading">
            <p>Redirigiendo...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="my-courses-page">
        <div className="my-courses-container">
          <div className="my-courses-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p>Cargando tus cursos...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-courses-page">
        <div className="my-courses-container">
          <div className="my-courses-error">
            <i className="fas fa-exclamation-triangle" />
            <h2>Error al cargar cursos</h2>
            <p>{error}</p>
            <button 
              className="btn btn-primary"
              onClick={fetchUserCourses}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Validación 2: Usuario sin cursos
  if (courses.length === 0) {
    return (
      <div className="my-courses-page">
        {/* Hero Section Empty */}
        <section className="my-courses-hero">
          <div className="my-courses-hero-content">
            <div className="my-courses-hero-text">
              <div className="my-courses-hero-badge">
                <span>TU BIBLIOTECA PERSONAL</span>
              </div>
              <h1 className="my-courses-hero-title">
                Mis <span className="my-courses-hero-title-accent">Cursos</span>
              </h1>
              <p className="my-courses-hero-description">
                Continúa tu aprendizaje y completa tus cursos. Accede a todo el contenido 
                que has adquirido y <strong>sigue avanzando en tu formación profesional</strong>.
              </p>
            </div>
            <div className="my-courses-hero-illustration">
              <div className="my-courses-hero-icon">
                <i className="fas fa-graduation-cap" />
              </div>
            </div>
          </div>
        </section>

        {/* Empty State */}
        <section className="my-courses-grid-section">
          <div className="my-courses-container">
            <div className="my-courses-empty">
              <div className="my-courses-empty-icon">
                <i className="fas fa-book-open" />
              </div>
              <h2>Aún no tienes cursos</h2>
              <p>Explora nuestro catálogo y encuentra el curso perfecto para ti.</p>
              <button 
                className="my-courses-empty-button"
                onClick={() => navigate('/courses')}
              >
                <i className="fas fa-shopping-bag" />
                Ver Catálogo de Cursos
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="my-courses-page">
      {/* Hero Section */}
      <section className="my-courses-hero">
        <div className="my-courses-hero-content">
          <div className="my-courses-hero-text">
            <div className="my-courses-hero-badge">
              <span>TU BIBLIOTECA PERSONAL</span>
            </div>
            <h1 className="my-courses-hero-title">
              Mis <span className="my-courses-hero-title-accent">Cursos</span>
            </h1>
            <p className="my-courses-hero-description">
              Continúa tu aprendizaje y completa tus cursos. Accede a todo el contenido 
              que has adquirido y <strong>sigue avanzando en tu formación profesional</strong>.
            </p>
            <div className="my-courses-hero-stats">
              <div className="my-courses-stat-item">
                <i className="fas fa-book-open" />
                <div>
                  <span className="my-courses-stat-value">{courses.length}</span>
                  <span className="my-courses-stat-label">Cursos</span>
                </div>
              </div>
              <div className="my-courses-stat-item">
                <i className="fas fa-check-circle" />
                <div>
                  <span className="my-courses-stat-value">
                    {courses.filter((c) => c.progress?.certificateEligible === true).length}
                  </span>
                  <span className="my-courses-stat-label">Completados</span>
                </div>
              </div>
              <div className="my-courses-stat-item">
                <i className="fas fa-chart-line" />
                <div>
                  <span className="my-courses-stat-value">
                    {Math.round(courses.reduce((acc, c) => acc + (c.progress?.progressPercent || 0), 0) / courses.length || 0)}%
                  </span>
                  <span className="my-courses-stat-label">Progreso Promedio</span>
                </div>
              </div>
            </div>
          </div>
          <div className="my-courses-hero-illustration">
            <div className="my-courses-hero-icon">
              <i className="fas fa-graduation-cap" />
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid Section */}
      <section className="my-courses-grid-section">
        <div className="my-courses-container">
          <div className="my-courses-grid">
          {courses.map((course) => {
            // Obtener progreso de la misma forma que MyCourseContentPage
            const progressPercent = course.progress?.progressPercent || 0;
            const totalClasses = course.progress?.totalClasses || course.modulos?.reduce((sum, m) => sum + (m.clases?.length || 0), 0) || 0;
            const completedClassesCount = course.progress?.completedClassesCount || 
                                         (Array.isArray(course.progress?.completedClasses) ? course.progress.completedClasses.length : 0) || 0;
            const certificateEligible = course.progress?.certificateEligible === true;
            const allClassesDone = progressPercent >= 100;
            const imageUrl = resolveImageUrl(course.imagen || course.image);

            return (
              <div 
                key={course.id} 
                className="my-courses-card"
                onClick={() => handleCourseClick(course.id)}
              >
                <div className="my-courses-card-image">
                  {imageUrl ? (
                    <img src={imageUrl} alt={course.titulo || course.nombre} />
                  ) : (
                    <div className="my-courses-card-placeholder">
                      <i className="fas fa-graduation-cap" />
                    </div>
                  )}
                  {certificateEligible && (
                    <div className="my-courses-card-badge">
                      <i className="fas fa-check-circle" />
                      Completado
                    </div>
                  )}
                </div>

                <div className="my-courses-card-content">
                  <h3 className="my-courses-card-title">
                    {course.titulo || course.nombre || 'Curso sin título'}
                  </h3>
                  
                  {course.descripcionBreve && (
                    <p className="my-courses-card-description">
                      {course.descripcionBreve}
                    </p>
                  )}

                  <div className="my-courses-card-progress">
                    <div className="my-courses-progress-header">
                      <span className="my-courses-progress-label">Progreso</span>
                      <span className="my-courses-progress-percentage">
                        {Math.round(progressPercent)}%
                      </span>
                    </div>
                    <div className="my-courses-progress-bar">
                      <div 
                        className="my-courses-progress-fill"
                        style={{
                          width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                          backgroundColor: getProgressColor(progressPercent)
                        }}
                      />
                    </div>
                    {totalClasses > 0 && (
                      <div className="my-courses-progress-stats">
                        <span>
                          <i className="fas fa-check-circle" />
                          {completedClassesCount} de {totalClasses} clases completadas
                        </span>
                      </div>
                    )}
                  </div>

                  {course.acquiredAt && (
                    <div className="my-courses-card-meta">
                      <i className="fas fa-calendar-alt" />
                      <span>Adquirido el {formatDate(course.acquiredAt)}</span>
                    </div>
                  )}

                  <div className="my-courses-card-actions">
                    <button type="button" className="my-courses-card-button">
                      {allClassesDone ? (
                        <>
                          <i className="fas fa-redo" />
                          Repasar Curso
                        </>
                      ) : (
                        <>
                          <i className="fas fa-play" />
                          Continuar Curso
                        </>
                      )}
                    </button>
                    {certificateEligible && (
                      <button
                        type="button"
                        className="my-courses-card-cert-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/my-courses/${course.id}/certificate`);
                        }}
                      >
                        <i className="fas fa-award" aria-hidden />
                        Ver certificado
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyCoursesPage;

